<template>
  <div ref="menu" class="navigation-mobile">
    <div class="navigation lg:hidden flex flex-col h-full">
      <div
        v-if="!currentAncestorTree"
        class="nav-headline"
      >
        Alle Kategorien
      </div>
      <div
        v-if="currentAncestorTree"
        class="nav-headline flex items-center justify-between"
      >
        <NuxtLink
          :to="basedir + parentCategoryURL"
          v-if="currentAncestor != '0'"
          class="whitespace-nowrap flex items-center justify-between"
          @click="currentCategory = '0'"
        >
          <div>{{ parentCategoryTitle }}</div>
        </NuxtLink>
        <div
          class="text-sm text-primary flex items-center"
          @click.prevent="
            currentTree = navigationTree;
            currentAncestorTree = null;
            parentCategoryURL = '';
          "
        >
          <BasicIcon
            :name="'chevron-right'"
            class="w-4 h-4 text-primary rotate-180"
          />
          Zurück
        </div>
      </div>
      <div class="nav-content">
        <template
          v-for="entry in getActiveTree()"
          :key="entry?.id"
        >
          <div 
            class="nav-item relative"
            :class="entry?.descendants && entry?.descendants?.length > 0 ? 'flex items-center justify-between':''"
          >
            <NuxtLink
              class="nav-link"
              :class="entry?.descendants && entry?.descendants?.length > 0 ? ' dropdown' : ''"
              target="_self"
              @click="
                currentTree = navigationTree;
                currentAncestorTree = null;
                navigateCurrentAncestorTree();
              "
              :to="basedir + (parentCategoryURL?.length > 0 ? parentCategoryURL + '/' : '') + entry?.url"
              :title="entry?.alt"
              :id="'dropdownMenu_' + entry?.id"
            >
              {{ entry?.name }}
            </NuxtLink>
            <div
              v-if="entry?.descendants && entry?.descendants?.length > 0"
              class="duration-300 ease-in-out top-0 right-0 h-full w-10 flex justify-end px-4 items-center grow"
              :class="currentCategory == entry?.id ? 'pointer-events-none' : ''"
              @click.prevent="
                parentCategoryTitle = entry?.name || '';
                parentCategoryURL =
                  (parentCategoryURL?.length > 0 ? parentCategoryURL + '/' : '') + (entry?.url || '');
                currentAncestor = entry?.id || '0';
                parentCategory = entry?.id || '0';
                currentAncestorTree = entry;
                currentCategory == entry?.id ? (currentCategory = '0') : (currentTree = entry?.descendants);
              "
            >
              <template v-if="currentCategory == entry?.id">
                <BasicIcon
                  :name="'chevron-right'"
                  class="w-4 h-4 rotate-90"
                />
              </template>
              <template v-else>
                <BasicIcon
                  :name="'chevron-right'"
                  class="w-4 h-4"
                />
              </template>
            </div>
          </div>
        </template>
        <template v-if="superCategoryTree">
          <div v-show="!currentAncestorTree">
            <div class="p-4"><hr /></div>
            <div
              v-for="superCategory in superCategoryTree"
              :key="superCategory?.id || superCategory?.extId"
              class="nav-item"
              :class="'group'"
            >
              <NuxtLink
                v-if="superCategory?.descendants"
                :id="'dropdownMenu_' + superCategory?.id"
                class="nav-link whitespace-nowrap flex items-center justify-between px-2 py-4"
                :class="superCategory?.descendants?.length > 0 ? ' dropdown' : ''"
                :to="superCategory?.url"
                :title="superCategory?.alt"
                external
              >
                {{ superCategory?.name }}
                <template v-if="superCategory?.descendants">
                  <span
                    v-if="superCategory?.descendants?.length > 0"
                    class="w-5 lg:ml-2 lg:hidden duration-300 ease-in-out"
                    :class="openSuperDropdown == superCategory?.id ? 'rotate-90' : ''"
                    @click.prevent="
                      openSuperDropdown == superCategory?.id
                        ? (openSuperDropdown = '0')
                        : (openSuperDropdown = superCategory?.id as string)
                    "
                  >
                    <BasicIcon
                      :name="'chevron-right'"
                      class="w-6 h-6 lg:hidden"
                    />
                  </span>
                </template>
              </NuxtLink>
              <template v-if="superCategory?.descendants && superCategory?.descendants?.length > 0">
                <div
                  :id="'dropdownContainer_' + superCategory?.id"
                  class="dropdown-container lg:container lg:mx-auto absolute transition invisible duration-500 ease-in-out group-hover:transform transform right-0 left-0 group-hover:opacity-100 group-hover:visible _z-10"
                  :class="openSuperDropdown == superCategory?.id ? ' !visible opacity-1' : ' opacity-0 hidden xl:block'"
                >
                  <template v-if="superCategory?.descendants">
                    <ul
                      class="navbar-nav list-style-none flex flex-col pl-0 lg:flex-row"
                      :class="superCategory?.descendants.length > 6 ? 'justify-between' : 'justify-start'"
                    >
                      <template
                        v-for="superChild in superCategory?.descendants"
                        :key="superChild.id || superChild?.extId"
                      >
                        <li class="nav-item">
                          <NuxtLink
                            class="nav-link px-2 py-4 lg:px-3 lg:py-5"
                            target="_self"
                            :to="superChild.url"
                            :title="superChild.name"
                            external
                          >
                            {{ superChild.name }}
                          </NuxtLink>
                        </li>
                      </template>
                    </ul>
                  </template>
                </div>
              </template>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useElementVisibility } from '@vueuse/core';
import type { MenuLink } from '~~/models/Navigation';
import { getExpNavigationTree } from '~/composables/ExpApi/expCache';
import { ProvideNames } from '#shared/statics';
import { inject } from 'vue';

interface Props {
  superCategoryTree: Array<MenuLink>;
}

defineProps<Props>();
const emit = defineEmits(['close-navigation'])

const menu = useTemplateRef('menu');
const visibility = useElementVisibility(menu);

const basedir = inject(ProvideNames.BASE_DIR);
const { data: navigationTree } = getExpNavigationTree('MAIN');
const currentTree = ref<MenuLink[] | null>(null);
const openSuperDropdown = useSuperMenuDropdownState() ?? ref('0');
const currentAncestorTree = ref<MenuLink | null>(null);
const currentCategory = ref('0');
const parentCategory = ref('0');
const parentCategoryTitle = ref('');
const parentCategoryURL = ref('');
const currentAncestor = ref('' as string | number);

function navigateCurrentAncestorTree() {
  currentAncestorTree.value = null;
  currentCategory.value = '0';
  parentCategory.value = '0';
  parentCategoryTitle.value = '';
  parentCategoryURL.value = '';
  currentAncestor.value = '';
}

function getActiveTree() {
  if (currentTree.value == null) {
    return navigationTree.value;
  } else {
    return currentTree.value;
  }
}

watch(visibility, (value, oldValue) => {
  if (oldValue === true && value === false) {
    emit('close-navigation');
  }
})
</script>
<style lang="postcss" scoped>
.navigation {
  @apply font-bold;
  & .nav-content {
    @apply overflow-auto;
    height: calc(100vh - 170px);
  }
  & .nav-headline {
    @apply whitespace-nowrap text-lg flex items-center justify-between px-4 py-4 text-paragraph-light;
  }
  & .nav-link {
    @apply whitespace-nowrap flex items-center justify-between px-4 py-4 text-sm;
  }
}
</style>
